// ============================================================
// HOUSE OF CARDS — AudioManager v1 — THE MIXER
// ------------------------------------------------------------
// One singleton owns all sound. App talks to it through a tiny
// verb set; it handles the messy parts (autoplay unlock, missing
// files, mute, the heartbeat-over-everything rule, hard cuts,
// jumpscare priority). Import the default export anywhere:
//
//     import Audio from "./AudioManager";
//     Audio.changeRoom("kitchen");      // hard-cut ambience
//     Audio.setHeartbeatTier(3);        // swap the pulse, keep it going
//     Audio.jumpscareSting("real");     // fire a sting (priority-gated)
//     Audio.enterStarPower();           // finale theme over the heartbeat
//     Audio.exitStarPower();
//     await Audio.mushroomRave();       // 2-min party, resolves when done
//     Audio.openMenu(); / Audio.closeMenu();
//     Audio.setMuted(true);
//
// DESIGN RULES (caP'n × V'ael, locked this session):
//   • HARD CUTS only — room/jumpscare/heartbeat swaps are instant.
//     If a player room-hops fast we never want tails piling up.
//   • HEARTBEAT plays OVER the room and NEVER gets interrupted by a
//     room change — only a tier change swaps the pulse track, and
//     even that is instant so the beat never stutters into silence.
//   • JUMPSCARE PRIORITY: real > masquerade > shadow. A higher sting
//     cuts a lower one already ringing; a lower one won't step on a
//     higher one mid-sting.
//   • MENU silences the house bed (room + heartbeat pause) and plays
//     the menu loop; closing resumes whatever was going.
//   • MUSHROOM RAVE takes the whole stage for 2 minutes: it pauses
//     the room + heartbeat, plays the party loop, and on end resumes
//     them. (Psychosis clearing is App's job — audio just parties.)
//   • Everything is crashproof: a missing/unloadable file logs once
//     and is treated as silence. The game never breaks for sound.
// ============================================================

import {
  AUDIO_TRACKS,
  ROOM_TRACK,
  STING_TRACK,
} from "./AudioAssets";

const HAS_DOM = typeof window !== "undefined" && typeof document !== "undefined";

// How long the mushroom rave runs, in ms. (2 minutes, locked.)
export const MUSHROOM_RAVE_MS = 120000;

class AudioManager {
  constructor() {
    this.els = {};                 // trackId → HTMLAudioElement (lazy)
    this.intent = {};              // trackId → true if it SHOULD be sounding now
    this.muted = false;
    this.unlocked = false;         // has a user gesture freed autoplay yet?
    this.warned = {};              // trackId → true once we've logged a failure

    this.currentRoom = null;       // room id whose ambience is playing
    this.currentHeartbeat = null;  // psychosis tier (0..4) currently recorded, or null
    this._heartbeatPlaying = null; // the ONE heartbeat trackId actually sounding (tier or star)
    this.activeSting = null;       // {type, priority} of a ringing sting
    this.starPower = false;        // is the secret sixth heartbeat owning the slot?
    this.raving = false;           // is the mushroom rave on?
    this.menuOpen = false;         // is the menu bed up (house paused)?
    this._raveTimer = null;

    if (HAS_DOM) this._installUnlock();
  }

  // ── AUTOPLAY UNLOCK ─────────────────────────────────────────
  // Browsers block sound until the user interacts. We listen once
  // for the first gesture and flip a flag; anything we tried to
  // start before then will start cleanly on the next call. We also
  // give all loaded elements a nudge so a queued bed begins.
  _installUnlock() {
    const unlock = () => {
      this.unlocked = true;
      // EVERY track we intended to play but that the phone speaker
      // throttled before unlock now gets a real, gesture-backed retry.
      // (Bluetooth happened to allow the early start; the phone speaker
      // did not — so the heartbeat was created, rejected, and never
      // retried. This is the fix: replay anything still flagged.)
      Object.keys(this.intent).forEach((trackId) => {
        if (this.intent[trackId]) this._kick(trackId);
      });
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    window.addEventListener("touchstart", unlock, { once: true });
  }

  // Gesture-time (re)start of a single track from the top of the gesture
  // stack — the only path mobile reliably honors on the phone speaker.
  _kick(trackId) {
    const el = this._el(trackId);
    if (!el) return;
    el.volume = this.muted ? 0 : this._baseVol(trackId);
    const p = el.play();
    if (p && p.catch) p.catch(() => {}); // still racing? harmless; intent stays set
  }

  // ── LAZY ELEMENT FACTORY ────────────────────────────────────
  // We build an <audio> the first time a track is asked for, so we
  // never pay for sounds a given session never reaches. Elements
  // are kept (looped beds especially) for instant re-use.
  _el(trackId) {
    if (!HAS_DOM) return null;
    if (this.els[trackId]) return this.els[trackId];
    const cfg = AUDIO_TRACKS[trackId];
    if (!cfg) {
      this._warn(trackId, `unknown track "${trackId}"`);
      return null;
    }
    const el = new Audio();
    el.src = cfg.src;
    el.loop = !!cfg.loop;
    el.preload = "auto";
    el.volume = this.muted ? 0 : (cfg.volume ?? 1);
    el.addEventListener("error", () => this._warn(trackId, `failed to load ${cfg.src}`));
    this.els[trackId] = el;
    return el;
  }

  _warn(trackId, msg) {
    if (this.warned[trackId]) return;
    this.warned[trackId] = true;
    // One quiet line per missing track — never throws, never spams.
    if (typeof console !== "undefined") {
      console.warn(`[AudioManager] ${msg} — treating as silence.`);
    }
  }

  _baseVol(trackId) {
    const cfg = AUDIO_TRACKS[trackId];
    return cfg ? (cfg.volume ?? 1) : 1;
  }

  // ── LOW-LEVEL PLAY / STOP ───────────────────────────────────
  _play(trackId, { restart = true } = {}) {
    const el = this._el(trackId);
    if (!el) return;
    // This track SHOULD be sounding now — remember that, so if the
    // phone speaker throttles the first start, the next user gesture
    // retries it (the heartbeat-only-on-bluetooth fix).
    this.intent[trackId] = true;
    if (restart) {
      try { el.currentTime = 0; } catch { /* not seekable yet */ }
    }
    el.volume = this.muted ? 0 : this._baseVol(trackId);
    // Try to start now; if we're not unlocked yet the play() will be
    // rejected and the unlock handler will _kick() it from a gesture.
    const p = el.play();
    if (p && p.catch) p.catch(() => {}); // autoplay race — harmless
  }

  _stop(trackId) {
    this.intent[trackId] = false;
    const el = this.els[trackId];
    if (!el) return;
    try {
      el.pause();
      el.currentTime = 0;
    } catch { /* ignore */ }
  }

  _pause(trackId) {
    // A pause is temporary (menu/rave) — keep intent so resume/unlock
    // brings it back. We only clear intent on a hard _stop.
    const el = this.els[trackId];
    if (!el) return;
    try { el.pause(); } catch { /* ignore */ }
  }

  _resume(trackId) {
    const el = this._el(trackId);
    if (!el) return;
    this.intent[trackId] = true;
    el.volume = this.muted ? 0 : this._baseVol(trackId);
    const p = el.play();
    if (p && p.catch) p.catch(() => {});
  }

  // ── ROOM AMBIENCE (hard-cut) ────────────────────────────────
  // Stop the old bed dead, start the new one. The heartbeat slot is
  // left entirely alone — it lives on its own layer (and Star Power
  // lives in THAT slot, not here). If we're mid-rave or mid-menu, we
  // DON'T start a room bed (the stage is taken); we just remember the
  // room so resuming later starts the right track.
  changeRoom(roomId) {
    const prev = this.currentRoom;
    this.currentRoom = roomId || null;
    if (this.menuOpen || this.raving) return; // stage is taken; remember only
    if (prev && ROOM_TRACK[prev]) this._stop(ROOM_TRACK[prev]);
    const next = ROOM_TRACK[this.currentRoom];
    if (next) this._play(next);
  }

  _stopRoom() {
    if (this.currentRoom && ROOM_TRACK[this.currentRoom]) {
      this._stop(ROOM_TRACK[this.currentRoom]);
    }
  }

  _startRoom() {
    if (this.currentRoom && ROOM_TRACK[this.currentRoom]) {
      this._play(ROOM_TRACK[this.currentRoom]);
    }
  }

  // ── HEARTBEAT SLOT (always on, ONE pulse at a time) ─────────
  // There is exactly ONE heartbeat playing, ever. Normally it's the
  // psychosis-tier pulse (0..4). Star Power is the secret SIXTH
  // heartbeat: when earned it TAKES OVER this same slot — it never
  // layers on top of the tier pulse (two hearts would sound absurd).
  // Dropping Star Power falls back to the current psychosis tier.
  //
  // Which track the slot should be playing right now:
  _heartbeatTrack() {
    if (this.starPower) return "star_power";
    if (this.currentHeartbeat === null) return null;
    return `heartbeat_${this.currentHeartbeat}`;
  }

  // Make the slot play the right track, hard-cutting whatever it was.
  // No-op if the correct track is already the one playing.
  _refreshHeartbeat() {
    const want = this._heartbeatTrack();
    if (want === this._heartbeatPlaying) return;          // already correct
    if (this._heartbeatPlaying) this._stop(this._heartbeatPlaying); // hard cut old
    this._heartbeatPlaying = want;
    if (!want) return;
    if (this.menuOpen || this.raving) return;             // slot is paused
    this._play(want);
  }

  // Set the psychosis pulse tier (0..4). If Star Power owns the slot
  // we still record the tier (so dropping Star Power falls back to the
  // right pulse) but we don't disturb the star heartbeat.
  setHeartbeatTier(tier) {
    const t = Math.max(0, Math.min(4, tier | 0));
    if (t === this.currentHeartbeat) return;
    this.currentHeartbeat = t;
    if (this.starPower) return;   // star power owns the slot; just remember the tier
    this._refreshHeartbeat();
  }

  _stopHeartbeat() {
    if (this._heartbeatPlaying) this._pause(this._heartbeatPlaying);
  }

  _startHeartbeat() {
    if (this._heartbeatPlaying) this._resume(this._heartbeatPlaying);
  }

  // Called when the player leaves the manor entirely (death, ending,
  // back to menu via the in-world button). Silences the house bed.
  stopHouse() {
    this._stopRoom();
    if (this._heartbeatPlaying) this._stop(this._heartbeatPlaying);
    this._heartbeatPlaying = null;
    this.currentHeartbeat = null;
    this.starPower = false;
  }

  // ── JUMPSCARE STINGS (priority-gated, one-shot) ─────────────
  // type: "real" | "masquerade" | "shadow". A higher-priority sting
  // cuts a lower one mid-ring; a lower one is ignored while a higher
  // one is still ringing. When a sting finishes it clears itself.
  jumpscareSting(type) {
    const trackId = STING_TRACK[type];
    if (!trackId) return;
    const cfg = AUDIO_TRACKS[trackId];
    const priority = cfg ? (cfg.priority ?? 1) : 1;

    if (this.activeSting) {
      if (priority < this.activeSting.priority) return;     // don't step on a bigger fright
      this._stop(STING_TRACK[this.activeSting.type]);       // cut the lesser one
    }

    this.activeSting = { type, priority };
    const el = this._el(trackId);
    if (el) {
      const clear = () => {
        if (this.activeSting && this.activeSting.type === type) this.activeSting = null;
        el.removeEventListener("ended", clear);
      };
      el.addEventListener("ended", clear);
    }
    this._play(trackId);
  }

  // ── STAR POWER (the secret sixth heartbeat) ─────────────────
  // Earned via the toy-in-gold-box ritual. It takes over the single
  // heartbeat slot from the psychosis-tier pulse — the room ambience
  // keeps playing underneath exactly as it does for any tier pulse.
  // Dropping it (death/loss) falls the slot back to the current
  // psychosis tier. Acts like an audio checkpoint: you respawn by the
  // gold box and re-earn it until you beat the boss in the Mouth.
  enterStarPower() {
    if (this.starPower) return;
    this.starPower = true;
    this._refreshHeartbeat();   // slot switches to star_power (hard cut)
  }

  exitStarPower() {
    if (!this.starPower) return;
    this.starPower = false;
    this._refreshHeartbeat();   // slot falls back to current psychosis tier
  }

  // ── MUSHROOM RAVE (2-min takeover) ──────────────────────────
  // Pauses the room + heartbeat, plays the party loop for exactly
  // two minutes, then resumes the bed. Returns a Promise that
  // resolves when the rave ends so App can clear psychosis right
  // as the music stops. Calling again while raving is a no-op
  // (one trip per... well, App enforces once-per-day).
  mushroomRave() {
    if (this.raving) return Promise.resolve();
    this.raving = true;
    this._stopRoom();
    this._stopHeartbeat();
    this._play("mushroom_rave");

    return new Promise((resolve) => {
      const end = () => {
        this._stop("mushroom_rave");
        this.raving = false;
        this._raveTimer = null;
        // Resume the bed where we left it (room + heartbeat).
        if (!this.menuOpen) {
          this._startRoom();
          this._startHeartbeat();
        }
        resolve();
      };
      this._raveTimer = setTimeout(end, MUSHROOM_RAVE_MS);
    });
  }

  // Hard-stop the rave early (e.g., leaving to menu). No psychosis
  // payoff — that only happens on a natural end via the Promise.
  cancelRave() {
    if (!this.raving) return;
    if (this._raveTimer) { clearTimeout(this._raveTimer); this._raveTimer = null; }
    this._stop("mushroom_rave");
    this.raving = false;
  }

  // ── MENU BED ────────────────────────────────────────────────
  // Open: pause the house (room + heartbeat), play the menu loop.
  // Close: stop the menu loop, resume whatever the house was doing.
  openMenu() {
    if (this.menuOpen) return;
    this.menuOpen = true;
    this._stopRoom();
    this._stopHeartbeat();
    this._play("menu");
  }

  closeMenu() {
    if (!this.menuOpen) return;
    this.menuOpen = false;
    this._stop("menu");
    if (this.raving) { this._play("mushroom_rave", { restart: false }); return; }
    this._startRoom();
    this._startHeartbeat();
  }

  // Start the menu bed cold (first load lands on the menu). Same as
  // openMenu but doesn't assume a house was running underneath.
  startMenu() {
    this.menuOpen = true;
    this._play("menu");
  }

  // ── MUTE ────────────────────────────────────────────────────
  // Respects the existing Settings "Mute" toggle. We keep tracks
  // running but at zero volume so loops stay in sync; flipping mute
  // off restores each track's configured level instantly.
  setMuted(muted) {
    this.muted = !!muted;
    Object.entries(this.els).forEach(([trackId, el]) => {
      if (el) el.volume = this.muted ? 0 : this._baseVol(trackId);
    });
  }

  isMuted() { return this.muted; }
}

// One mixer for the whole app.
const instance = new AudioManager();
export default instance;
